import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { paymentBankingService } from "services";
import {
  PaymentBankingForm,
  PaymentBankingQuery,
  PaymentBankingUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type PaymentBankingListKey =
  | ["payment-bankings"]
  | ["payment-bankings", PaymentBankingQuery];
type PaymentBankingDetailKey = ["payment-bankings", "detail", number | string];

const paymentBankingKeys = {
  all: ["payment-bankings"] as const,
  list: (params?: PaymentBankingQuery): PaymentBankingListKey =>
    params ? ["payment-bankings", params] : ["payment-bankings"],
  detail: (id: number | string): PaymentBankingDetailKey => [
    "payment-bankings",
    "detail",
    id,
  ],
};

export function usePaymentBanking() {
  const queryClient = useQueryClient();

  const fetchList = (params?: PaymentBankingQuery, enabled = true) => {
    return useQuery({
      queryKey: paymentBankingKeys.list(params),
      queryFn: () => paymentBankingService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: data?.totalPage ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: paymentBankingKeys.detail(id),
      queryFn: () => paymentBankingService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createPaymentBanking = useMutation({
    mutationFn: (payload: PaymentBankingForm) =>
      paymentBankingService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo phiếu chi ngân hàng thành công");
      }
      queryClient.invalidateQueries({ queryKey: paymentBankingKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo phiếu chi ngân hàng thất bại");
      console.error("createPaymentBanking failed", err);
    },
  });

  const updatePaymentBanking = useMutation({
    mutationFn: (payload: PaymentBankingUpdatePayload) =>
      paymentBankingService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Cập nhật phiếu chi ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: paymentBankingKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật phiếu chi ngân hàng thất bại");
      console.error("updatePaymentBanking failed", err);
    },
  });

  const deletePaymentBanking = useMutation({
    mutationFn: (id: number | string) => paymentBankingService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá phiếu chi ngân hàng thành công");
      }
      queryClient.invalidateQueries({ queryKey: paymentBankingKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá phiếu chi ngân hàng thất bại");
      console.error("deletePaymentBanking failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createPaymentBanking,
    updatePaymentBanking,
    deletePaymentBanking,
  };
}
